import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Trash, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ExportButton from "@/components/ExportButton";
import { API } from "@/lib/api";
import { formatDateEU, todayISO } from "@/lib/constants";

const REASONS = ["Overproduction", "Damage", "Expired", "Quality issue", "Spillage", "Broken Bottle", "Tasting/Internal", "Other"];
const UNITS = ["units", "L", "ml"];

const client = axios.create({ baseURL: API, withCredentials: false });
client.interceptors.request.use((config) => {
  const tok = typeof window !== "undefined" ? window.localStorage.getItem("garnacha_token") : null;
  if (tok) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tok}`;
  }
  return config;
});

const blank = () => ({
  product_id: "",
  quantity: 1,
  unit: "units",
  reason: REASONS[0],
  notes: "",
  waste_date: todayISO(),
  linked_batch_id: "",
});

export default function Waste() {
  const [records, setRecords] = useState([]);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());

  const load = async () => {
    const [w, p, b] = await Promise.all([
      client.get("/waste"),
      client.get("/products", { params: { active: true } }),
      client.get("/production-batches"),
    ]);
    setRecords(w.data);
    setProducts(p.data);
    setBatches(b.data);
  };
  useEffect(() => { load(); }, []);

  const totalUnits = useMemo(
    () => records.reduce((a, r) => a + (Number(r.quantity) || 0), 0),
    [records]
  );

  // By batch + by product roll-ups
  const byBatch = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const k = r.linked_batch_id || "— (no batch)";
      if (!map[k]) map[k] = { count: 0, qty: 0 };
      map[k].count += 1;
      map[k].qty += Number(r.quantity) || 0;
    });
    return Object.entries(map).sort((a, b) => b[1].qty - a[1].qty);
  }, [records]);

  const byProduct = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const k = r.product_name || "—";
      if (!map[k]) map[k] = { count: 0, qty: 0 };
      map[k].count += 1;
      map[k].qty += Number(r.quantity) || 0;
    });
    return Object.entries(map).sort((a, b) => b[1].qty - a[1].qty);
  }, [records]);

  const openCreate = () => { setForm(blank()); setOpen(true); };

  const save = async () => {
    if (!form.product_id) return toast.error("Pick a product");
    if (!form.quantity || Number(form.quantity) <= 0) return toast.error("Quantity must be > 0");
    try {
      await client.post("/waste", { ...form, quantity: Number(form.quantity) });
      toast.success("Waste recorded — inventory decremented");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record waste");
    }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete waste record for ${r.product_name}? Inventory will be restored.`)) return;
    try {
      await client.delete(`/waste/${r.id}`);
      toast.success("Waste deleted, inventory restored");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="page-waste">
      <PageHeader
        overline="Loss Tracking"
        title="Waste"
        subtitle={`${records.length} records · ${totalUnits.toFixed(0)} units lost · auto-decrements inventory`}
        actions={
          <div className="flex gap-2">
            <ExportButton module="waste" />
            <Button variant="outline" onClick={load} data-testid="reload-waste"><RefreshCw size={16} className="mr-2" /> Refresh</Button>
            <Button onClick={openCreate} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="add-waste-btn">
              <Plus size={16} className="mr-2" /> Record Waste
            </Button>
          </div>
        }
      />

      {records.length === 0 ? (
        <EmptyState icon={Trash} title="No waste recorded" description="Log spillage, broken bottles, expired stock or quality rejects. Inventory adjusts automatically." />
      ) : (
        <>
          {/* Summary panels */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div className="card-soft p-4" data-testid="waste-by-batch">
              <div className="brand-overline mb-2">By Batch</div>
              <div className="max-h-40 overflow-auto divide-y divide-[#E5E5E5]">
                {byBatch.map(([bid, v]) => (
                  <div key={bid} className="flex justify-between py-1.5 text-sm" data-testid={`waste-batch-${bid}`}>
                    <span className="font-mono text-[#1F4D3A]">{bid}</span>
                    <span className="text-[#E76F51] font-bold">{v.qty.toFixed(1)} <span className="text-[10px] text-[#6C757D] font-normal">({v.count} record{v.count === 1 ? "" : "s"})</span></span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card-soft p-4" data-testid="waste-by-product">
              <div className="brand-overline mb-2">By Product</div>
              <div className="max-h-40 overflow-auto divide-y divide-[#E5E5E5]">
                {byProduct.map(([name, v]) => (
                  <div key={name} className="flex justify-between py-1.5 text-sm">
                    <span className="text-[#222]">{name}</span>
                    <span className="text-[#E76F51] font-bold">{v.qty.toFixed(1)} <span className="text-[10px] text-[#6C757D] font-normal">({v.count})</span></span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        <div className="card-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F1E8] text-[#1F4D3A] text-[10px] uppercase tracking-wider font-bold">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Batch</th>
                <th className="text-left px-4 py-3">Qty</th>
                <th className="text-left px-4 py-3">Reason</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">By</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id} className="border-t border-[#E5E5E5] hover:bg-[#F7F1E8]/30" data-testid={`waste-row-${r.id}`}>
                  <td className="px-4 py-3 text-xs text-[#6C757D]">{formatDateEU(r.waste_date)}</td>
                  <td className="px-4 py-3 font-semibold text-[#222]">{r.product_name}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs font-mono text-[#1F4D3A]">{r.linked_batch_id || "—"}</td>
                  <td className="px-4 py-3 font-bold text-[#E76F51]">−{r.quantity} <span className="text-[10px] text-[#6C757D] font-normal">{r.unit || "units"}</span></td>
                  <td className="px-4 py-3 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-[#E76F51]/15 text-[#E76F51] uppercase tracking-wider font-bold text-[10px]">{r.reason}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#6C757D] hidden lg:table-cell">{r.recorded_by || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="icon" onClick={() => del(r)} className="text-[#E76F51] hover:bg-[#E76F51]/10" data-testid={`delete-waste-${r.id}`}>
                      <Trash2 size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="waste-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Record Waste</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Product *" className="col-span-2">
              <Select value={form.product_id || "__none"} onValueChange={(v) => setForm({ ...form, product_id: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="input-waste-product"><SelectValue placeholder="Pick…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— Pick —</SelectItem>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} · {p.category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Linked Batch" className="col-span-2">
              <Select value={form.linked_batch_id || "__none"} onValueChange={(v) => setForm({ ...form, linked_batch_id: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="input-waste-batch"><SelectValue placeholder="— pick batch (recommended)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— None —</SelectItem>
                  {batches.slice(0, 30).map((b) => (
                    <SelectItem key={b.id} value={b.batch_id}>{b.batch_id} · {b.product}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Quantity *">
              <Input type="number" min="0" step="0.5" value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value === "" ? "" : Number(e.target.value) })}
                data-testid="input-waste-qty" />
            </Field>
            <Field label="Unit">
              <Select value={form.unit || "units"} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger data-testid="input-waste-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {UNITS.map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Date" className="col-span-2">
              <Input type="date" value={form.waste_date || ""}
                onChange={(e) => setForm({ ...form, waste_date: e.target.value })}
                data-testid="input-waste-date" />
            </Field>
            <Field label="Reason" className="col-span-2">
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger data-testid="input-waste-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Notes" className="col-span-2">
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-waste">Cancel</Button>
            <Button onClick={save} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="save-waste">Record & Decrement Stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">{label}</Label>
    {children}
  </div>
);
